import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { MediaImage } from '../../../types/storefront.types';
import Accordion from '../../components/accordion/accordion';
import Badge from '../../components/badge/badge';
import Icon from '../../components/icon';
import styles from '../../components/product.module.scss';
import ProductForm from '../../components/product-form/product-form';
import ProductMedia from '../../components/product-media/product-media';
import { getProduct } from '../../server/catalog.functions';
import { prices } from '../../types/shipping';
import type { Variant } from '../../types/types';
import { formatPrice } from '../../utils/format';
import { percentage } from '../../utils/percentage';
import { notFoundSeo, seo, toAbsoluteUrl } from '../../utils/seo';
import { nullIfNotPrerendered } from '../../utils/static-data';
import { getProductIdFromShopifyId } from '../../utils/shopify-helper';
import {
  productBreadcrumbJsonLd,
  productJsonLd
} from '../../utils/structured-data';

export const Route = createFileRoute('/_layout/product/$handle')({
  loader: async ({ params }) => {
    const product = await getProduct({
      data: { handle: params.handle }
    }).catch(nullIfNotPrerendered);

    if (!product) {
      throw notFound();
    }

    return { product };
  },
  head: ({ loaderData }) => {
    // the loader threw notFound()
    if (!loaderData) {
      return { meta: notFoundSeo() };
    }

    const { product } = loaderData;

    return {
      meta: seo({
        title: `${product.title} motivációs karkötő - Inspiráló üzenet, kényelmes viselet, kétoldalú design`,
        description: product.description,
        image:
          product.media.nodes[0]?.previewImage?.url ??
          product.images.nodes[0]?.url
      }),
      scripts: [productJsonLd(product), productBreadcrumbJsonLd(product)]
    };
  },
  component: ProductDetailRoute
});

function ProductDetailRoute() {
  const { product } = Route.useLoaderData();
  const [selectedVariant, setSelectedVariant] = useState(
    product.variants.nodes[0]
  );
  const isNewProduct = product.tags.includes('új');

  // Get product id from Shopify GID for Stamped reviews
  const productIdForStampedReviews = getProductIdFromShopifyId(product.id);

  const handleVariantChange = (variant: Variant) => {
    setSelectedVariant(variant);
  };

  const getDiscount = () =>
    `-${100 - percentage(Number(selectedVariant.price.amount), Number(selectedVariant.compareAtPrice?.amount))}%`;

  // the prices are MoneyV2 objects, their amounts are decimal strings
  const hasCompareAtPrice = () =>
    !!selectedVariant.compareAtPrice &&
    Number(selectedVariant.price.amount) <
      Number(selectedVariant.compareAtPrice.amount);

  useEffect(() => {
    // Initializing the Stamped reviews widget on the product detail page. Needs to do this here because the widget needs to be initialized after the script is loaded, and we are loading the script in the root route. We also need to do this in a useEffect to make sure it runs only on the client side.
    window.StampedFn?.init({
      apiKey: import.meta.env.VITE_STAMPED_API_KEY,
      storeUrl: import.meta.env.VITE_SHOPIFY_STORE_DOMAIN
    });
  }, []);

  return (
    <div className="container-fluid">
      <div className="page-content centered">
        <>
          <div className={styles.product}>
            <div className={styles.productLeft}>
              <ProductMedia
                media={product.media.nodes}
                alt={`${product.title} karkötő`}
              />
            </div>
            <div className={styles.productInfo}>
              <h1 className={styles.productTitle}>{product.title}</h1>
              <div className={styles.productPricesWrapper}>
                <div
                  className={`${styles.productPrices} ${hasCompareAtPrice() ? styles.hasCompareAtPrice : ''}`}
                >
                  <span
                    className={styles.productPrice}
                    aria-label={`${hasCompareAtPrice() ? 'Akciós ár' : 'Ár'}`}
                  >
                    {formatPrice(selectedVariant.price.amount)}
                  </span>
                  {hasCompareAtPrice() && (
                    <span
                      className={styles.productCompareAtPrice}
                      aria-label="Eredeti ár"
                    >
                      {formatPrice(selectedVariant.compareAtPrice!.amount)}
                    </span>
                  )}
                </div>
                <div className={styles.badges}>
                  {hasCompareAtPrice() && <Badge>{getDiscount()}</Badge>}
                  {isNewProduct && (
                    <Badge backgroundColor="aquamarine">ÚJ</Badge>
                  )}
                </div>
              </div>
              <div
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
              />
              <ProductForm
                product={product}
                onVariantChange={handleVariantChange}
                selectedVariant={selectedVariant}
              />
              <div className={styles.features}>
                <ul className={styles.featuresList}>
                  <li className={styles.featuresListItem}>
                    <Icon className={styles.icon} name="sun" size={20} />
                    Pozitív üzenet minden napra: Egyetlen pillantás, és máris
                    emlékszel, hogy képes vagy többre.
                  </li>
                  <li className={styles.featuresListItem}>
                    <Icon className={styles.icon} name="feather" size={20} />
                    Rugalmas, kényelmes viselet: Könnyű textil anyaga szinte
                    észrevétlenül simul a csuklódra.
                  </li>
                  <li className={styles.featuresListItem}>
                    <Icon className={styles.icon} name="repeat" size={20} />
                    Kétoldalú design: Cseréld a stílust egyetlen mozdulattal, és
                    viseld úgy, ahogy Neked tetszik!
                  </li>
                  <li className={styles.featuresListItem}>
                    <Icon className={styles.icon} name="star" size={20} />
                    Egyedi minták: Válogass változatos mintáink közül, hogy
                    megtaláld a Hozzád legjobban illőt.
                  </li>
                </ul>
              </div>
              <div>
                <Accordion id="material" title="Anyag és ápolás">
                  Rugalmas textil, 100%-ban újrahasznosított, környezetbarát
                  eljárással készült poliészterből és elasztánból.
                  <br />
                  Tisztítsd hideg vagy langyos vízzel, puha szivacs vagy
                  törlőrongy segítségével, majd hagyd a levegőn megszáradni.
                  <br />A karkötő szélessége 15 mm, vastagsága 2 mm.
                </Accordion>
                <Accordion id="shipping" title="Szállítás és visszaküldés">
                  <ul className={styles.shippingModes}>
                    <li>
                      <Icon className={styles.icon} name="mail" size={20} />{' '}
                      Postai levél <span className="separator dash" />{' '}
                      <em>{formatPrice(prices.mail)}</em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="package" size={20} />{' '}
                      FOXPOST csomagautomata <span className="separator dash" />{' '}
                      <em>{formatPrice(prices.parcelLocker)}</em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="truck" size={20} />{' '}
                      Házhozszállítás futárral{' '}
                      <span className="separator dash" />{' '}
                      <em>{formatPrice(prices.homeDelivery)}</em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="package" size={20} />{' '}
                      Csomagautomatába, Posta Pontra vagy postára kézbesítve:{' '}
                      <em>
                        bruttó{' '}
                        {formatPrice(prices.magyarPostaParcelLockerOrToPost)}
                        /db
                      </em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="truck" size={20} />{' '}
                      Háznál történő kézbesítés esetén: 0-10 kg-ig{' '}
                      <em>
                        bruttó{' '}
                        {formatPrice(prices.magyarPostaHomeDeliveryUpTo10kg)}
                        /db
                      </em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="truck" size={20} />{' '}
                      Háznál történő kézbesítés esetén: 10-20 kg-ig{' '}
                      <em>
                        bruttó{' '}
                        {formatPrice(prices.magyarPostaHomeDeliveryUpTo20kg)}
                        /db
                      </em>
                    </li>
                    <li>
                      <Icon className={styles.icon} name="truck" size={20} />{' '}
                      Háznál történő kézbesítés esetén: 20-40 kg-ig{' '}
                      <em>
                        bruttó{' '}
                        {formatPrice(prices.magyarPostaHomeDeliveryUpTo40kg)}
                        /db
                      </em>
                    </li>
                  </ul>
                  Szeretnéd visszaváltani a karkötőd? Küldd vissza{' '}
                  <Link to="/contact">címünkre</Link> a terméket eredeti
                  állapotában a kézhezvételétől számítva 14 napon belül, és mi
                  igény szerint kicseréljük vagy visszatérítjük az árát.
                </Accordion>
                <Accordion id="faq" title="Kérdésed van?">
                  Mielőtt felveszed velünk a kapcsolatot, fusd át a{' '}
                  <Link to="/faq">gyakori kérdéseket</Link>. Ha itt nem találod
                  meg a választ a kérdésedre, keress minket bátran valamelyik{' '}
                  <Link to="/contact">elérhetőségünkön</Link>.
                </Accordion>
              </div>
            </div>
          </div>
          <div
            id="stamped-main-widget"
            data-product-id={productIdForStampedReviews}
            data-name={product.title}
            data-url={toAbsoluteUrl(`/product/${product.handle}`)}
            data-image-url={(product.media.nodes[0] as MediaImage).image?.url}
            data-description={product.description}
            data-product-sku={product.handle}
          ></div>
        </>
      </div>
    </div>
  );
}
